import {
  Message,
  useCreateMessageMutation,
  useDeleteMessageMutation,
  useUpdateMessageMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseMessageMutationProps = {}

export type UseMessageMutationResult = {
  createMessage: (message: Partial<Message>, onSuccess: (() => void) | undefined) => void
  updateMessage: (message: Partial<Message>, onSuccess: (() => void) | undefined) => void
  deleteMessage: (messageId: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useMessageMutation = (props: UseMessageMutationProps = {}): UseMessageMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createMessage, {isLoading: isCreating}] = useCreateMessageMutation()
  const [_updateMessage, {isLoading: isUpdating}] = useUpdateMessageMutation()
  const [_deleteMessage, {isLoading: isDeleting}] = useDeleteMessageMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useMessageUpdate_creating",
        title: t("common.status.creating", "Creating"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useMessageUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useMessageUpdate_updating",
        title: t("common.status.updating", "Updating"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useMessageUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useMessageUpdate_deleting",
        title: t("common.status.deleting", "Deleting"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useMessageUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<void>, onSuccess: (() => void) | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.status.success", "Success"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.status.success", "Success"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((messageId: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteMessage({id: messageId}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteMessage, handleDeleteResult])

  const createMessage = useCallback((message: Partial<Message>, onSuccess: (() => void) | undefined) => {
    const promise = _createMessage(message).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createMessage, handleCreateResult])

  const updateMessage = useCallback((message: Partial<Message>, onSuccess: (() => void) | undefined) => {
    const promise = _updateMessage(message).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateMessage, handleUpdateResult])

  const deleteMessage = useCallback((messageId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.confirm", "Are you sure you want to delete?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(messageId, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createMessage: createMessage,
    updateMessage: updateMessage,
    deleteMessage: deleteMessage,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
